import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { FreightEditViewModel } from "TypeGen/Order/freight-edit-view-model";

type Props = {
  orderFreight: FreightEditViewModel;
  setOrderFreight: React.Dispatch<React.SetStateAction<FreightEditViewModel>>;
};

const FreightEditUI: React.FC<Props> = ({ orderFreight, setOrderFreight }) => {
  const pieces = orderFreight.Pieces > 0 ? orderFreight.Pieces : '';
  const weight = orderFreight.Weight > 0 ? orderFreight.Weight : '';
  const length = orderFreight.Length > 0 ? orderFreight.Length : '';
  const width = orderFreight.Width > 0 ? orderFreight.Width : '';
  const height = orderFreight.Height > 0 ? orderFreight.Height : '';
  const stackableName = orderFreight.StackableLimit ? `Stack ${orderFreight.StackableLimit}` : orderFreight.Stackable ? 'Stackable' : 'Not Stackable';
  return <>
    <div className="form-row">
      <div className="form-group col-md-2 text-right">
        <label className="col-form-label">Pieces</label>
      </div>
      <div className="form-group col-md-5">
        <div className="input-group">
          <input
            type="number"
            className="form-control"
            placeholder="Pieces"
            value={pieces}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setOrderFreight({ ...orderFreight, Pieces: parseInt(e.target.value || '0') })}
          />
          <div className="input-group-append">
            <button className="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
              {orderFreight.PiecesUnitOfMeasures.find(x => x.ID === orderFreight.PiecesUnitOfMeasure)?.Name}
            </button>
            <div className="dropdown-menu">
              {orderFreight.PiecesUnitOfMeasures.map(x => <button key={x.ID} type="button" className="dropdown-item" onClick={() => setOrderFreight({ ...orderFreight, PiecesUnitOfMeasure: x.ID })}>{x.Name}</button>)}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="form-row">
      <div className="form-group col-md-2 text-right">
        <label className="col-form-label">Weight</label>
      </div>
      <div className="form-group col-md-5">
        <div className="input-group">
          <input
            className="form-control"
            type="number"
            min={0}
            value={weight}
            placeholder="Weight"
            onFocus={(e) => e.target.select()}
            onChange={(e) => setOrderFreight({ ...orderFreight, Weight: parseFloat(e.target.value || '0') })}
          />
          <div className="input-group-append">
            <button className="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
              {orderFreight.WeightUnitOfMeasure === 1 ? 'LBS' : 'KGS'}
            </button>
            <div className="dropdown-menu">
              <button type="button" className="dropdown-item" onClick={() => setOrderFreight({ ...orderFreight, WeightUnitOfMeasure: 1 })}>Pounds</button>
              <button type="button" className="dropdown-item" onClick={() => setOrderFreight({ ...orderFreight, WeightUnitOfMeasure: 2 })}>Kilograms</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="form-row">
      <div className="form-group col-md-2 text-right">
        <label className="col-form-label">Dimensions</label>
      </div>
      <div className="form-group col-md-10">
        <div className="input-group">
          <input
            type="number"
            className="form-control"
            placeholder="L"
            value={length}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setOrderFreight({ ...orderFreight, Length: parseInt(e.target.value || '0') })}
          />
          <div className="input-group-prepend">
              <span className="input-group-text">X</span>
          </div>
          <input
            type="number"
            className="form-control"
            placeholder="W"
            value={width}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setOrderFreight({ ...orderFreight, Width: parseInt(e.target.value || '0') })}
          />
          <div className="input-group-prepend">
              <span className="input-group-text">X</span>
          </div>
          <input
            type="number"
            className="form-control"
            placeholder="H"
            value={height}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setOrderFreight({ ...orderFreight, Height: parseInt(e.target.value || '0') })}
          />
          <div className="input-group-append">
            <button
              type="button"
              title="Rotatable"
              onClick={() => setOrderFreight({ ...orderFreight, Rotatable: !orderFreight.Rotatable })}
              className="btn btn-outline-secondary"
              style={{ borderRadius: 0, paddingTop: 0, paddingBottom: 0 }}
            >
              <svg aria-hidden="true" focusable="false" width="1.25rem" height="1.25rem" style={{ transform: 'rotate(360deg)' }} preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24">
                <path fill={orderFreight.Rotatable ? "#007bff" : "#6c757d"} d="M7.34 6.41L.86 12.9l6.49 6.48 6.49-6.48-6.5-6.49zM3.69 12.9l3.66-3.66L11 12.9l-3.66 3.66-3.65-3.66zm15.67-6.26A8.95 8.95 0 0 0 13 4V.76L8.76 5 13 9.24V6c1.79 0 3.58.68 4.95 2.05a7.007 7.007 0 0 1 0 9.9 6.973 6.973 0 0 1-7.79 1.44l-1.49 1.49C10.02 21.62 11.51 22 13 22c2.3 0 4.61-.88 6.36-2.64a8.98 8.98 0 0 0 0-12.72z" />
              </svg>
            </button>
            <button className="btn btn-outline-secondary dropdown-toggle" style={{ borderRadius: 0 }} type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
              {orderFreight.DimsUnitOfMeasure === 2 ? 'IN' : 'FT'}
            </button>
            <span>
              <div className="dropdown-menu">
                <button type="button" className="dropdown-item" onClick={() => setOrderFreight({ ...orderFreight, DimsUnitOfMeasure: 2 })}>Inches</button>
                <button type="button" className="dropdown-item" onClick={() => setOrderFreight({ ...orderFreight, DimsUnitOfMeasure: 1 })}>Feet</button>
              </div>
            </span>
            <span>
              <button className="btn btn-outline-secondary dropdown-toggle" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }} type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                {stackableName}
              </button>
              <div className="dropdown-menu">
                <button type="button" className="dropdown-item" onClick={() => setOrderFreight({ ...orderFreight, Stackable: true, StackableLimit: 0 })}>Stackable</button>
                <button type="button" className="dropdown-item" onClick={() => setOrderFreight({ ...orderFreight, Stackable: false, StackableLimit: 0 })}>Not Stackable</button>
                <button type="button" className="dropdown-item" onClick={() => setOrderFreight({ ...orderFreight, Stackable: true, StackableLimit: 2 })}>Stack 2</button>
                <button type="button" className="dropdown-item" onClick={() => setOrderFreight({ ...orderFreight, Stackable: true, StackableLimit: 3 })}>Stack 3</button>
                <button type="button" className="dropdown-item" onClick={() => setOrderFreight({ ...orderFreight, Stackable: true, StackableLimit: 4 })}>Stack 4</button>
                <button type="button" className="dropdown-item" onClick={() => setOrderFreight({ ...orderFreight, Stackable: true, StackableLimit: 5 })}>Stack 5</button>
              </div>
            </span>
          </div>
        </div>
      </div>
    </div>
    <div className="form-row">
      <div className="form-group col-md-2 text-right">
        <label className="col-form-label">Commodity</label>
      </div>
      <div className="form-group col-md-5">
        <DropDownList
          value={orderFreight.Commodities.find(x => x.ID == orderFreight.CommodityID)}
          data={orderFreight.Commodities}
          textField="Name"
          className="w-100"
          onChange={(e) => setOrderFreight({ ...orderFreight, CommodityID: e.target.value.ID })}
        />
        <Input
          placeholder="Commodity Alt. Description"
          value={orderFreight.CommodityAltDescription}
          onChange={(e) => setOrderFreight({ ...orderFreight, CommodityAltDescription: e.value })}
          className="mt-2 w-100"
        />
      </div>
    </div>
    <div className="form-row">
      <div className="form-group col-md-2 text-right">
        <label className="col-form-label">Bill of Lading</label>
      </div>
      <div className="form-group col-md-5">
        <Input
          value={orderFreight.BillOfLadingNumber}
          onChange={(e) => setOrderFreight({ ...orderFreight, BillOfLadingNumber: e.value })}
          style={{ width: "100%" }}
        />
      </div>
    </div>
    <div className="form-row">
      <div className="form-group col-md-2 text-right">
        <label className="col-form-label">Freight Value</label>
      </div>
      <div className="form-group col-md-5">
        <div className="input-group">
          <NumericTextBox
            className="form-control"
            min={0}
            value={orderFreight.FreightValue}
            placeholder="$0.00"
            format="c2"
            spinners={false}
            onChange={(e) => setOrderFreight({ ...orderFreight, FreightValue: e.value })}
          />
        </div>
      </div>
    </div>
    <div className="form-row">
      <div className="form-group col-md-2 text-right">
        <label className="col-form-label">POD Signed By</label>
      </div>
      <div className="form-group col-md-5">
        <Input
          value={orderFreight.ProofOfDeliverySignedBy}
          onChange={(e) => setOrderFreight({ ...orderFreight, ProofOfDeliverySignedBy: e.value })}
          style={{ width: "100%" }}
        />
      </div>
    </div>
    {orderFreight.PickupStopOptions.length > 0 && <>
      <div className="form-row">
        <div className="form-group col-md-2 text-right">
          <label className="col-form-label">Pickup Stop</label>
        </div>
        <div className="form-group col-md-5">
          <DropDownList
            value={orderFreight.PickupStopOptions.find(x => x.ID == orderFreight.PickupStopID)}
            data={orderFreight.PickupStopOptions}
            textField="Name"
            className="w-100"
            onChange={(e) => setOrderFreight({ ...orderFreight, PickupStopID: e.target.value.ID })}
          />
        </div>
      </div>
    </>}
    {orderFreight.DeliveryStopOptions.length > 0 && <>
      <div className="form-row">
        <div className="form-group col-md-2 text-right">
          <label className="col-form-label">Delivery Stop</label>
        </div>
        <div className="form-group col-md-5">
          <DropDownList
            value={orderFreight.DeliveryStopOptions.find(x => x.ID == orderFreight.DeliveryStopID)}
            data={orderFreight.DeliveryStopOptions}
            textField="Name"
            className="w-100"
            onChange={(e) => setOrderFreight({ ...orderFreight, DeliveryStopID: e.target.value.ID })}
          />
        </div>
      </div>
    </>}
  </>
}

export default FreightEditUI;
